import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AccountLayout from '../components/account/AccountLayout';
import AccountNav from '../components/account/AccountNav';
import { useAuth } from '../context/AuthContext';
import { useAccount, useUpdateAccount } from '../hooks/useAccount';

const AccountPage = () => {
  const navigate = useNavigate();
  const { accessToken, user } = useAuth();
  const { data: account, isLoading: accountLoading, error: accountError } = useAccount(accessToken);
  const updateAccount = useUpdateAccount(accessToken);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [profileMessage, setProfileMessage] = useState('');
  const [profileError, setProfileError] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [initialNames, setInitialNames] = useState({ first: '', last: '' });

  useEffect(() => {
    if (account) {
      const profile = account.profile || {};
      setFirstName(profile.first_name || profile.firstName || account.first_name || '');
      setLastName(profile.last_name || profile.lastName || account.last_name || '');
      setInitialNames({
        first: profile.first_name || profile.firstName || account.first_name || '',
        last: profile.last_name || profile.lastName || account.last_name || '',
      });
    }
  }, [account]);

  const handleSaveName = async (event) => {
    event.preventDefault();
    if (!accessToken) {
      setProfileError('Missing access token');
      return;
    }
    setNameSaving(true);
    setProfileError('');
    setProfileMessage('');
    try {
      await updateAccount.mutateAsync({ first_name: firstName, last_name: lastName });
      setProfileMessage('Profile updated');
    } catch (err) {
      setProfileError(err.message || 'Unable to update profile');
    } finally {
      setNameSaving(false);
    }
  };

  const handleNav = (key) => {
    if (key === 'account') navigate('/account');
    else if (key === 'integrations') navigate('/account/integrations');
  };

  return (
    <AccountLayout
      nav={
        <AccountNav
          activeKey="account"
          onSelect={handleNav}
          onBack={() => navigate('/')}
        />
      }
    >
      <header>
        <h1 className="text-2xl font-semibold text-slate-50">Account</h1>
      </header>
      <div className="rounded-xl border border-white/10 bg-white/5 p-4 md:p-6">
        <h2 className="text-lg font-semibold text-slate-50">{`${
          account?.profile?.first_name || account?.first_name || ''
        } ${account?.profile?.last_name || account?.last_name || ''}`}</h2>
        {accountLoading && <p className="mt-2 text-sm text-slate-300">Loading account…</p>}
        {accountError && <p className="mt-2 text-sm text-red-300">{accountError.message || 'Unable to load account'}</p>}
        {!accountLoading && !accountError && (
          <>
            <div className="mt-6 mb-6 flex gap-6 flex-col sm:flex-row">
              <div className="flex flex-col items-start gap-2">
                <div className="flex h-24 w-24 md:h-28 md:w-28 items-center justify-center overflow-hidden rounded-full bg-white/10 text-2xl md:text-3xl font-bold text-slate-50">
                  {account?.avatar_url ? (
                    <img
                      src={account.avatar_url}
                      alt={account?.name || account?.email || 'avatar'}
                      className="h-24 w-24 md:h-28 md:w-28 rounded-full object-cover"
                    />
                  ) : (
                    `${(account?.profile?.first_name || account?.first_name || account?.name || ' ')[0] || ''}${
                      (account?.profile?.last_name || account?.last_name || ' ')[0] || ''
                    }`
                  )}
                </div>
              </div>
            </div>
            
            <span className="text-sm text-slate-100">{account?.email || user?.email || '—'}</span>

            <form onSubmit={handleSaveName} className="mt-6 flex flex-col gap-3">
              <div className="flex flex-col gap-3 w-full sm:w-1/2 md:w-1/3 min-w-[220px]">
                <label className="flex flex-col gap-1 text-sm text-slate-200">
                  First Name
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-100 outline-none"
                    placeholder="First name"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm text-slate-200">
                  Last Name
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-100 outline-none"
                    placeholder="Last name"
                  />
                </label>
              </div>
              <div className="flex items-center gap-3 justify-end">
                {profileMessage && <span className="text-sm text-emerald-300">{profileMessage}</span>}
                {profileError && <span className="text-sm text-red-300">{profileError}</span>}
                <button
                  type="submit"
                  disabled={
                    nameSaving ||
                    (firstName === initialNames.first && lastName === initialNames.last)
                  }
                  className="rounded-lg bg-gradient-to-r from-amber-500 to-sky-500 px-4 py-2 text-sm font-semibold text-[#050815] shadow-lg shadow-sky-500/30 disabled:opacity-40"
                >
                  {nameSaving ? 'Saving…' : 'Save Profile'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </AccountLayout>
  );
};

export default AccountPage;
